import React, {useContext, useEffect, useState} from 'react';
import { toast } from '../../../../ui/toast';
import { DataTable, TableColumn } from '../../../../ui'; // Assuming TableColumn<T> is generic
import { Applicant, TabData } from '../../../../utils/types/JobOpenings';
import {
  CCA_SUBDOMAINS,
  DUMMY_CCA_RESULTS,
} from '../../../questions/CCABank/ccaDummyData';
import { Column, GetColumns } from './Functions/GetColumns';
import {useSelector} from "react-redux";
import {RootState} from "../../../../utils/redux_store/store";
import {JobOpeningsContext} from "../../../../utils/contexts/JobOpening";

const DUMMY_CCA_RESULTS_ARRAY = Object.values(DUMMY_CCA_RESULTS);

type TableViewProps<T> = {
  candidates: Applicant[];
  loading?: boolean;
  tabTitles: string[];
  setTabTitles: (titles: string[]) => void | undefined;
  selectedCandidates: TabData[];
  setSelectedCandidates: (tabDataObject: TabData[]) => void | undefined;
  columns: Column[];
  setColumns: (value: ((prevState: Column[]) => Column[]) | Column[]) => void;
  getColumns: TableColumn<T>[];
  matchedSkills: string[];
  setActiveTab: (value: number) => void;
  aiScoreWeights?: { [key: string]: number };
  onMoveToUnsuccessful?: (candidates: Applicant[]) => void;
  onBulkStatusChange?: (
    candidates: Applicant[],
    targetStatus: string,
  ) => Promise<void> | void;
  onCandidateClick?: (
    candidate: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string,
  ) => void;
  // Selection props (shared with 2D/3D chart)
  selectedApplicantsForEmail?: Applicant[];
  handleApplicantSelection?: (
    applicant: Applicant,
    isSelected?: boolean,
  ) => void;
  isApplicantSelected?: (applicant: Applicant) => boolean;
  selectAllUnsuccessful?: () => void;
  clearSelection?: () => void;
  handleSendEmailToSelected?: () => void;
  currentTab?: string;
};

type FormattedApplicant = {
  id: string;
  Applicant: string;
  Email: string;
  Avatar: string;
  'CV Score': string;
  Status: string;
  Location: string;
  City: string;
  Country: string;
  Experience: string;
  Age: string;
  Gender: string;
  Skills: string;
  [key: string]: string | number | boolean;
};

export const TableView = <T extends object>({
  candidates,
  loading = false,
  selectedCandidates,
  setSelectedCandidates,
  tabTitles,
  setTabTitles,
  getColumns,
  matchedSkills,
  setActiveTab,
  aiScoreWeights,
  onMoveToUnsuccessful,
  onBulkStatusChange,
  onCandidateClick,
  selectedApplicantsForEmail = [],
  handleApplicantSelection,
  isApplicantSelected,
  selectAllUnsuccessful,
  clearSelection,
  handleSendEmailToSelected,
  currentTab,
}: TableViewProps<FormattedApplicant>) => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [targetStatus, setTargetStatus] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<
    { key: string; direction: 'asc' | 'desc' }[]
  >([{ key: 'CV Score', direction: 'desc' }]);
  const [showCCAColumns, setShowCCAColumns] = useState(false);
  const [retakenCandidates, setRetakenCandidates] = useState<Set<string>>(
    new Set(),
  );
  const [retakeConfirmId, setRetakeConfirmId] = useState<string | null>(null);
  const rowsPerPage = 25;

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const { jobOpeningElements } = useContext(JobOpeningsContext);

  const onPageChange = (page: number) => {
    const maxPages = Math.max(1, Math.ceil(candidates.length / rowsPerPage));
    const nextPage = Math.min(Math.max(page, 1), maxPages);
    setPageNumber(nextPage);
  };

  const sortCandidates = (
    data: Applicant[],
    sortKey: string,
    direction: 'asc' | 'desc',
  ) => {
    return [...data].sort((a, b) => {
      let aValue: number | string = '';
      let bValue: number | string = '';

      if (sortKey === 'CV Score' || sortKey === 'aiScore') {
        aValue = a.aiScore ?? 0;
        bValue = b.aiScore ?? 0;
      } else if (sortKey === 'Experience') {
        aValue = a.experience ?? a.value?.[1] ?? 0;
        bValue = b.experience ?? b.value?.[1] ?? 0;
      } else if (sortKey === 'Age') {
        aValue = a.age ?? 0;
        bValue = b.age ?? 0;
      } else if (sortKey === 'Skills') {
        aValue = a.noOfSkills ?? a.skills?.length ?? 0;
        bValue = b.noOfSkills ?? b.skills?.length ?? 0;
      } else {
        aValue = (a as any)[sortKey] ?? '';
        bValue = (b as any)[sortKey] ?? '';
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'desc' ? bValue - aValue : aValue - bValue;
      } else {
        return direction === 'desc'
          ? String(bValue).localeCompare(String(aValue))
          : String(aValue).localeCompare(String(bValue));
      }
    });
  };

  // onSortChange from DataTable
  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSortConfig([{ key, direction }]);
  };

  const onClickTab = (row: FormattedApplicant) => {
    const candidate = candidates.find((item) => item.userId === row.id);

    // Use the new onCandidateClick prop if available, otherwise use the old tab system
    if (onCandidateClick && candidate) {
      onCandidateClick(candidate);
      return;
    }

    // Fallback to old tab system
    const nameExists = selectedCandidates.some(
      (item) => item.candidate && item.candidate.userId === row.id,
    );

    if (!nameExists) {
      const updatedTabTitles = [...tabTitles, row.Applicant];
      const selectedItem: Applicant | undefined = candidates.find(
        (item) => item.userId === row.id,
      );
      const newSelectedCandidates = [
        ...selectedCandidates,
        {
          tabId: tabTitles.length,
          candidate: selectedItem,
        },
      ];
      setSelectedCandidates(newSelectedCandidates);
      setTabTitles(updatedTabTitles);
      setActiveTab(tabTitles.length);
    } else {
      toast.error('Tab for ' + row.Applicant + ' already open', {
        position: 'bottom-center',
      });
    }
  };

  const formatApplicants = (data: Applicant[]): FormattedApplicant[] => {
    return data.map((applicant, idx) => {
      const yoe = applicant.experience ?? applicant.value?.[1] ?? 0;
      const city = applicant.locationCity || '';
      const country = applicant.locationCountry || '';
      const skillsCount = applicant.noOfSkills ?? applicant.skills?.length ?? 0;

      const formatted: FormattedApplicant = {
        id: applicant.userId,
        Applicant: `${applicant.firstName ?? ''} ${
          applicant.lastName ?? ''
        }`.trim(),
        Email: applicant.email || '',
        Avatar: applicant.base64Encoded || '',
        'CV Score': `${applicant.aiScore}%`,
        Status: applicant.applicantStatus,
        Location: [city, country].filter(Boolean).join(', '),
        City: city,
        Country: country,
        Experience: yoe ? `${yoe} yr${yoe === 1 ? '' : 's'}` : '—',
        Age: applicant.age != null ? String(applicant.age) : '—',
        Gender: applicant.gender || '—',
        Skills: skillsCount ? String(skillsCount) : '—',
      };

      matchedSkills.forEach((skill) => {
        const hasSkill = applicant.skills.some(
          (matched) => matched.toLowerCase() === skill.toLowerCase(),
        );

        const upperCased = skill.toUpperCase();
        formatted[upperCased] = hasSkill ? 'Yes' : 'No';
      });

      // Add CCA data if available
      const dummyIdx = idx % DUMMY_CCA_RESULTS_ARRAY.length;
      const ccaResult = retakenCandidates.has(applicant.userId)
        ? {
          status: 'invite-sent' as const,
          totalScore: null,
          subdomainScores: {},
        }
        : DUMMY_CCA_RESULTS_ARRAY[dummyIdx];

      formatted['CCA Status'] = retakenCandidates.has(applicant.userId)
        ? 'invite-sent'
        : ccaResult.status;
      formatted['CCA Total'] =
        ccaResult.totalScore != null ? `${ccaResult.totalScore}%` : '—';
      CCA_SUBDOMAINS.forEach((sd) => {
        const score = (ccaResult as any).subdomainScores?.[sd];
        formatted[`CCA ${sd}`] = score != null ? `${score}%` : '—';
      });

      return formatted;
    });
  };

  const sortedCandidates =
    sortConfig.length > 0
      ? sortCandidates(candidates, sortConfig[0].key, sortConfig[0].direction)
      : candidates;

  const changedData = formatApplicants(sortedCandidates);
  const totalPages = Math.max(1, Math.ceil(changedData.length / rowsPerPage));

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const paginatedData = changedData.slice(
    (pageNumber - 1) * rowsPerPage,
    pageNumber * rowsPerPage,
  );

  const moveStatusOptions = [
    'New',
    'Suitable',
    'Outstanding',
    'Shortlisted',
    'Auto-Assessment',
    'Unsuccessful-Pending',
    ...(jobOpeningElements?.includes('Wellness') ? ['Wellness'] : []),
    ...(jobOpeningElements?.includes('Agentic Assessment') ? ['Agentic-Assessment'] : []),
    ...(jobOpeningElements?.includes('Psychometric')
        ? ['Psychometric']
        : []),
  ];

  const isUnsuccessfulTab = currentTab === 'Unsuccessful Applicants';
  const selectedCount = selectedApplicantsForEmail.length;

  const ccaStatusColors: Record<string, string> = {
    'invite-sent': 'bg-slate-100 text-slate-600',
    'in-progress': 'bg-amber-100 text-amber-700',
    completed: 'bg-emerald-100 text-emerald-700',
  };

  const ccaColumns: TableColumn<FormattedApplicant>[] = showCCAColumns
    ? [
      {
        key: 'CCA Status',
        label: 'CCA Status',
        disableSort: true,
        render: (row) => {
          const status = String(row['CCA Status'] || '');
          return (
            <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                  ccaStatusColors[status] ?? 'bg-slate-100 text-slate-500'
                }`}
            >
              { status.replace('-', ' ') }
            </span>
          );
        },
      },
      {
        key: 'CCA Total',
        label: 'CCA Total',
        align: 'center' as const,
      },
      ...CCA_SUBDOMAINS.map((sd) => ({
        key: `CCA ${sd}`,
        label: sd
          .split(' ')
          .map((w) => w[0])
          .join(''),
        align: 'center' as const,
        disableSort: true,
      })),
      {
        key: 'cca-retake',
        label: 'Retake',
        disableSort: true,
        align: 'center' as const,
        render: (row: FormattedApplicant) => (
          <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                setRetakeConfirmId(row.id);
              }}
              className='px-2 py-1 rounded-lg text-[11px] font-semibold bg-violet-50 text-violet-600 border border-violet-200 hover:bg-violet-100 transition-colors'
          >
            Retake
          </button>
        ),
      },
    ]
    : [];

  const allColumns = [...getColumns, ...ccaColumns];

  // Determine if all visible table applicants are selected
  const allSelected =
    candidates.length > 0 && candidates.every((c) => isApplicantSelected?.(c));

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection?.();
    } else {
      candidates.forEach((candidate) => {
        handleApplicantSelection?.(candidate, true);
      });
    }
  };

  const handleRowSelect = (_rowIndex: number, row: FormattedApplicant) => {
    const candidate = candidates.find((c) => c.userId === row.id);
    if (candidate) {
      handleApplicantSelection?.(candidate);
    }
  };

  const isRowSelected = (_rowIndex: number, row: FormattedApplicant) => {
    const candidate = candidates.find((c) => c.userId === row.id);
    return candidate ? isApplicantSelected?.(candidate) ?? false : false;
  };

  const handleRowClick = (row: FormattedApplicant) => {
    // Keep row click and checkbox selection as separate actions.
    // Row click opens candidate profile/tab, checkbox controls selection.
    onClickTab(row);
  };

  return (
    <div className='relative w-full h-full overflow-x-auto p-4'>
      { /* Retake CCA Confirmation Modal */ }
      { retakeConfirmId && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm'>
          <div className='bg-white rounded-2xl shadow-2xl border border-slate-100 p-6 w-full max-w-sm'>
            <h3 className='text-base font-bold text-slate-800 mb-1'>
              Retake CCA?
            </h3>
            <p className='text-sm text-slate-500 mb-5'>
              This will reset the candidate&apos;s CCA status to &quot;Invite
              Sent&quot; and clear their current scores.
            </p>
            <div className='flex justify-end gap-2'>
              <button
                  type='button'
                  onClick={() => setRetakeConfirmId(null)}
                  className='px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors'
              >
                Cancel
              </button>
              <button
                  type='button'
                  onClick={() => {
                    setRetakenCandidates(
                      (prev) =>
                        new Set(Array.from(prev).concat(retakeConfirmId!)),
                    );
                    setRetakeConfirmId(null);
                  }}
                  className='px-4 py-2 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 transition-colors'
              >
                Confirm Retake
              </button>
            </div>
          </div>
        </div>
      ) }

      { /* Show CCA toggle */ }
      <div className='mb-2 flex items-center justify-end'>
        <button
            type='button'
            onClick={() => setShowCCAColumns((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
              showCCAColumns
                ? 'bg-violet-600 text-white border-violet-600 shadow-md shadow-violet-100'
                : 'bg-white text-violet-600 border-violet-200 hover:bg-violet-50'
            }`}
        >
          <svg
              className='w-3.5 h-3.5'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth={2}
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
            />
          </svg>
          { showCCAColumns ? 'Hide CCA' : 'Show CCA' }
        </button>
      </div>
      { aiScoreWeights !== null && (
        <div className='mb-3 p-2 rounded-lg bg-gradient-to-br from-white/90 to-gray-100/90 flex items-center gap-2 border border-gray-100 shadow-sm backdrop-blur-sm'>
          <div className='font-semibold text-gray-900 text-sm tracking-tight flex items-center gap-1.5 shrink-0'>
            <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                className='text-green-500'
            >
              <circle
                  cx='12'
                  cy='12'
                  r='10'
                  fill='currentColor'
                  opacity='0.12'
              />
              <path
                  d='M8 12.5L11 15.5L16 10.5'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
              />
            </svg>
            CV Score Weights
          </div>
          <div className='flex flex-wrap gap-1.5'>
            { Object.entries(aiScoreWeights ?? {})
              .filter(([key]) => key.toLowerCase() !== 'keyword')
              .map(([key, value]) => (
                <div
                    key={key}
                    className='flex flex-col items-center justify-center px-2 py-1 rounded-md bg-white/90 border border-gray-100 min-w-[70px]'
                >
                  <span className='text-[9px] uppercase tracking-wide text-gray-500 leading-tight font-medium'>
                    { key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (str) => str.toUpperCase()) }
                  </span>
                  <span className='text-lg font-bold text-gray-900 leading-tight'>
                    { value }%
                  </span>
                </div>
              )) }
          </div>
        </div>
      ) }
      { /* Selection Action Bar */ }
      { handleApplicantSelection && (
        <div
            className={`mb-3 flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-200 ${
              selectedCount > 0
                ? 'bg-blue-50 border-blue-200 shadow-sm'
                : 'bg-gray-50 border-gray-200'
            }`}
        >
          { /* Select All Unsuccessful */ }
          { isUnsuccessfulTab && (
            <button
                onClick={handleSelectAll}
                className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide bg-white border border-blue-200 text-blue-600 hover:bg-blue-100 transition-all'
            >
              <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              { allSelected ? 'Deselect All' : 'Select All Pending' }
            </button>
          ) }

          { /* Selected count */ }
          <span className='text-xs font-semibold text-slate-600'>
            { selectedCount > 0 ? (
              <span className='text-blue-700'>{ selectedCount } selected</span>
            ) : (
              <span className='text-slate-400'>
                No selection — use checkboxes to select applicants
              </span>
            ) }
          </span>

          <div className='flex-1' />

          { /* Move to Unsuccessful */ }
          { onBulkStatusChange && selectedCount > 0 && (
            <div className='flex items-center gap-2'>
              <select
                  value={targetStatus}
                  onChange={(e) => setTargetStatus(e.target.value)}
                  className='px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 bg-white text-slate-700'
              >
                <option value=''>Move to status...</option>
                { moveStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    { status }
                  </option>
                )) }
              </select>
              <button
                  onClick={() => {
                    if (!targetStatus) return;
                    onBulkStatusChange(selectedApplicantsForEmail, targetStatus);
                    setTargetStatus('');
                  }}
                  disabled={!targetStatus}
                  className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide bg-orange-50 border border-orange-200 text-orange-600 hover:bg-orange-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <svg
                    className='w-3.5 h-3.5'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                  />
                </svg>
                Apply
              </button>
            </div>
          ) }

          { !onBulkStatusChange && onMoveToUnsuccessful && selectedCount > 0 && (
            <button
                onClick={() => onMoveToUnsuccessful(selectedApplicantsForEmail)}
                className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide bg-orange-50 border border-orange-200 text-orange-600 hover:bg-orange-100 transition-all'
            >
              <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z'
                />
              </svg>
              Move to Unsuccessful
            </button>
          ) }

          { /* Send Email */ }
          { handleSendEmailToSelected && selectedCount > 0 && (
            <button
                onClick={handleSendEmailToSelected}
                className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 transition-all'
            >
              <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                />
              </svg>
              Email ({ selectedCount })
            </button>
          ) }

          { /* Clear selection */ }
          { selectedCount > 0 && (
            <button
                onClick={clearSelection}
                className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide bg-white border border-slate-200 text-slate-500 hover:bg-slate-100 transition-all'
            >
              <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                />
              </svg>
              Clear
            </button>
          ) }
        </div>
      ) }

      <DataTable
          isLoading={loading}
          columns={allColumns as TableColumn<FormattedApplicant>[]}
          data={paginatedData}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          currentPage={pageNumber}
          onClickRow={handleRowClick}
          totalPages={totalPages}
          totalRecords={changedData.length}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
          hasCheckBox={!!handleApplicantSelection}
          onSelectAll={handleSelectAll}
          onSelectRow={handleRowSelect}
          isRowSelected={isRowSelected}
          isAllSelected={allSelected}
      />
    </div>
  );
};
